import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface AgeVerificationProps {
  open: boolean;
  onVerified: () => void;
  onCancel: () => void;
}

export const AgeVerification = ({ open, onVerified, onCancel }: AgeVerificationProps) => {
  const [birthdate, setBirthdate] = useState('');
  const [parentalConsent, setParentalConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const calculateAge = (birthdate: string): number => {
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleVerify = () => {
    if (!birthdate) {
      toast.error('Please enter your date of birth');
      return;
    }

    if (!termsAccepted) {
      toast.error('You must accept the terms to continue');
      return;
    }

    const age = calculateAge(birthdate);

    if (age < 16) {
      toast.error('You must be at least 16 years old to make payments on HiStage');
      return;
    }

    if (age < 18 && !parentalConsent) {
      toast.error('Users under 18 require parental consent');
      return;
    }

    // Store verification in session
    sessionStorage.setItem('age-verified', 'true');
    sessionStorage.setItem('age-verification-date', new Date().toISOString());
    
    toast.success('Age verified successfully');
    onVerified();
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            Age Verification Required
          </DialogTitle>
          <DialogDescription>
            Payment features require age verification. Users must be at least 16 years old, and those under 18 need parental consent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="birthdate">Date of Birth</Label>
            <Input
              id="birthdate"
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {birthdate && calculateAge(birthdate) < 18 && calculateAge(birthdate) >= 16 && (
            <div className="flex items-start space-x-2 p-3 bg-muted rounded-lg">
              <Checkbox
                id="parental-consent"
                checked={parentalConsent}
                onCheckedChange={(checked) => setParentalConsent(checked as boolean)}
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="parental-consent"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I have parental or guardian consent
                </label>
                <p className="text-xs text-muted-foreground">
                  Required for users under 18 years old
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start space-x-2 p-3 bg-muted rounded-lg">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I accept the Terms of Use and Privacy Policy
              </label>
              <p className="text-xs text-muted-foreground">
                By proceeding, you confirm the information provided is accurate
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleVerify} className="flex-1">
            Verify & Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
